import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { useDeleteProduct, useProducts } from "@/api/products-query";
import { AlertDialog } from "@/components/dashboard/alert-dialog";
import { ExpandingSearchBar } from "@/components/dashboard/search-bar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor/store/")({
	component: StorePage
});

function StorePage() {
	const { data, isPending } = useProducts();
	const [value, setValue] = useState("");

	const deleteProduct = useDeleteProduct();
	const products = useMemo(() => {
		if (!data)
			return [];
		return data.items.filter(product => product.name.toLowerCase().includes(value.toLowerCase()));
	}, [data, value]);
	return (
		<section className="flex flex-col space-y-4">
			<div className="flex items-center justify-between gap-2">
				<ExpandingSearchBar placeholder="Search products" className="ml-auto" value={value} onChange={e => setValue(e.target.value)} />
				<Button asChild>
					<Link to="/vendor/store/create">
						<Plus className="h-4 w-6" />
						Add Product
					</Link>
				</Button>
			</div>

			<section className={cn("min-h-[80vh] p-3 bg-gray-50 rounded-xl flex  gap-4 flex-wrap h-full items-center justify-center", {
				"items-start justify-start": products.length
			})}
			>
				{
					isPending
						? (
								<div className="w-full h-96 flex items-center justify-center">
									<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
								</div>
							)
						: products.length
							? (
									<div className="w-full grid grid-cols-[repeat(auto-fit,_minmax(250px,_250px))] max-sm:grid-cols-1 gap-2">
										{products.map(product => (
											<Card
												key={product.id}
												className="p-2 space-y-4 bg-gray-100 shadow-none"
											>
												<CardHeader className="w-full h-40 bg-white rounded-xl overflow-hidden">
													<img
														src={product.photos.length ? product.photos[0] : "/placeholder.svg"}
														className="object-cover w-full h-full"
														alt={product.name}
													/>
												</CardHeader>
												<CardContent className="p-1 text-ellipsis overflow-hidden">
													<div className="flex items-center justify-between">
														<div className="flex flex-col w-full">
															<div className="flex items-center justify-between gap-2">
																<div>
																	<CardTitle className="capitalize">
																		{product.name}
																	</CardTitle>
																	<Button asChild variant="link" className="p-0 m-0 justify-start">
																		<Link
																			to="/vendor/store/$productid"
																			params={{ productid: product.id }}
																			key={product.id}
																		>
																			more details
																		</Link>
																	</Button>
																</div>
																<AlertDialog
																	description="This action cannot be undone. This will permanently delete your this product."
																	onClick={() => deleteProduct.mutate(product.id)}
																>
																	<Button
																		size="icon"
																		variant="ghost"
																		className="p-2 hover:bg-destructive/50 rounded-full"

																	>
																		<Trash2 className="text-destructive" />
																	</Button>
																</AlertDialog>
															</div>
														</div>
														<div className="flex items-center gap-2">
														</div>
													</div>
												</CardContent>
											</Card>
										))}
									</div>

								)
							: (
									<div className="space-y-4 *:text-center">
										<div className="m-auto flex items-center justify-center bg-hover-secondary size-48 rounded-full">
											<img src="/no-products.png" alt="contract icon" />
										</div>
										<div>
											<h3 className="text-lg font-bold">You've not added any products yet</h3>
											<p className="text-muted-foreground">Add a list of your products and they will appear here</p>
										</div>
									</div>
								)
				}
			</section>
		</section>
	);
}
